// markup/takeoff.mjs — DropPin measure ROLL-UP (pure, headless).
// ---------------------------------------------------------------------------
// The quantity-surveying read-out that makes the measure tool professional:
// every MEASURE mark summed into a total length + total area, broken down by
// label/kind, respecting the current calibration + unit. NO React / DOM.
//
// The grand totals come from the SHARED computeTakeOffTotals (@insitebg/plan-markup-core
// totals.mjs) via a small adapter, so DropPin's numbers are produced by the
// exact same engine InSpecPDF prices real work with — the measure marks are
// mapped into that engine's takeOffData shape (length -> 'polyline', area ->
// 'area', the calibration -> mmPerPdfPt). The per-group breakdown + the display
// strings come from ../measure (itself the shared core's polyline/area/format).
// There is no parallel measurement maths in this file.
// ---------------------------------------------------------------------------

import {
  computeTakeOffTotals, formatLength, formatArea, resolveAutoDecimals,
} from '@insitebg/plan-markup-core';
import { measureObject, resolveCalibration, AREA_UNIT_FOR } from './measure.mjs';

const DRAW = 'd'; // single synthetic drawing id (DropPin markup is per drawing)

// Map DropPin measure marks -> the vendored engine's takeOffData shape. Length
// measures (polylines) carry lengthPt (natural px); area measures carry areaPt;
// the calibration becomes calibrationByDrawing[DRAW].mmPerPdfPt (our unit of
// measurement space is the natural IMAGE PIXEL, the exact analogue of a PDF pt).
export function toTakeOffData(objects, W, H, calibration) {
  const cal = resolveCalibration(calibration, W, H);
  const ms = [];
  for (const o of objects || []) {
    if (o.type !== 'measure') continue;
    const r = measureObject(o, W, H, null, 'm'); // px magnitudes only
    if (o.measureKind === 'area') ms.push({ type: 'area', areaPt: r.areaPx, pageNum: 1 });
    else ms.push({ type: 'polyline', lengthPt: r.lengthPx, pageNum: 1 });
  }
  const data = { measurementsByDrawing: { [DRAW]: ms } };
  if (cal) data.calibrationByDrawing = { [DRAW]: { mmPerPdfPt: cal.mmPerImagePx } };
  return data;
}

// Full take-off summary for the panel:
//   { calibrated, unit, areaUnit,
//     length:{ mm, px, count, label }, area:{ mm2, px, count, label },
//     groups:[{ key, kind, count, label }], core }
// `core` is the raw vendored computeTakeOffTotals result (grand totals). Groups
// are keyed by the mark's `label` when set, else by kind ('Length' / 'Area').
export function computeMarkupTakeoff(objects, W, H, calibration, unit = 'm', decimalsPref = 'auto', typeNames = null) {
  const cal = resolveCalibration(calibration, W, H);
  const calibrated = !!cal;
  const dec = resolveAutoDecimals(decimalsPref, unit === 'mm');
  const areaUnit = AREA_UNIT_FOR[unit] || 'm2';

  // Grand totals from the vendored engine (bit-identical to InSpecPDF pricing).
  const core = computeTakeOffTotals(toTakeOffData(objects, W, H, calibration));

  // Per-group breakdown + formatted strings (via the shared measure/format core).
  const groups = new Map();
  let lengthCount = 0, areaCount = 0;
  for (const o of objects || []) {
    if (o.type !== 'measure') continue;
    const kind = o.measureKind === 'area' ? 'area' : 'length';
    // 2026-08-14 — group by the mark's CATALOGUE TYPE first. That is what turns
    // a plan into a take-off: "new tiles 42 m², full regrout 18 m" instead of
    // one undifferentiated "Area 60 m²". A hand-typed label still wins when
    // there is one (someone naming a specific room said something more precise
    // than the type did), then the type, then the bare kind.
    //
    // typeNames maps key -> the tenant's current label, so renaming a type in
    // Settings renames it everywhere at once. Unknown key falls back to the key
    // itself: a total attributed to "full-regrout" is still readable, whereas
    // dropping it would lose the quantity.
    const typeName = o.typeKey
      ? ((typeNames && typeNames[o.typeKey]) || o.typeKey)
      : null;
    const key = (o.label && String(o.label).trim())
      || typeName
      || (kind === 'area' ? 'Area' : 'Length');
    const r = measureObject(o, W, H, calibration, unit, decimalsPref);
    let g = groups.get(key);
    if (!g) { g = { key, kind, count: 0, mm: 0, mm2: 0, px: 0 }; groups.set(key, g); }
    g.count += 1;
    if (kind === 'area') { areaCount += 1; g.mm2 += r.mm2 || 0; g.px += r.areaPx || 0; }
    else { lengthCount += 1; g.mm += r.mm || 0; g.px += r.lengthPx || 0; }
  }

  const groupRows = Array.from(groups.values()).map((g) => ({
    key: g.key,
    kind: g.kind,
    count: g.count,
    label: g.kind === 'area'
      ? (calibrated ? formatArea(g.mm2, areaUnit, dec) : `${Math.round(g.px)} px²`)
      : (calibrated ? formatLength(g.mm, unit, dec) : `${Math.round(g.px)} px`),
  }));

  return {
    calibrated,
    unit,
    areaUnit,
    length: {
      mm: core.lengthMm, px: core.lengthPt, count: lengthCount,
      label: calibrated ? formatLength(core.lengthMm, unit, dec) : `${Math.round(core.lengthPt)} px`,
    },
    area: {
      mm2: core.areaMm2, px: core.areaPt2, count: areaCount,
      label: calibrated ? formatArea(core.areaMm2, areaUnit, dec) : `${Math.round(core.areaPt2)} px²`,
    },
    groups: groupRows,
    core,
  };
}

// 2026-09-15 — the LEGEND's right-hand column: what each catalogue type adds up
// to on this drawing. Keyed by the mark's TYPE only. Unlike the panel's groups,
// a hand-typed label does not move a mark out of its type here, because the
// legend's promise is "red is new tiles", whatever the room is called.
// No marks -> '—'. No scale -> a count, since px² means nothing on a stamp.
export function legendTotals(objects, W, H, calibration, unit = 'm', keys = []) {
  const out = {};
  for (const key of keys || []) {
    const marks = (objects || []).filter((o) => o && o.type === 'measure' && o.typeKey === key);
    if (!marks.length) { out[key] = '—'; continue; }
    const t = computeMarkupTakeoff(marks, W, H, calibration, unit);
    if (!t.calibrated) { out[key] = marks.length === 1 ? '1 mark' : `${marks.length} marks`; continue; }
    const parts = [];
    if (t.area.count) parts.push(t.area.label);
    if (t.length.count) parts.push(t.length.label);
    out[key] = parts.join(' · ');
  }
  return out;
}
